import React from 'react'
import type { TranslationKey } from '../../i18n'
import type { LotEventType, LotLocationType, LotStage } from '../../types/lot'
import type { LotStoryProjectionV1 } from '../../types/lotStory'
import {
  formatOptionalStoryNumber,
  formatOptionalStoryText,
  getStoryFamilyLabel,
  getStoryFamilyTechnicalId
} from './lotStoryPresentation'
import { getLotStoryReadinessLabel } from './lotStoryLibraryUi'
import {
  deriveLotStoryReadinessCue,
  type LotStoryReadinessCueLevel,
  type LotStoryReadinessCueReason
} from './lotStoryReadinessCue'

type TranslateFn = (key: TranslationKey, vars?: Record<string, string | number>) => string

type LotStoryCollaborationPreviewProps = {
  projection: LotStoryProjectionV1
  t: TranslateFn
}

const stageLabelKey: Record<LotStage, TranslationKey> = {
  Active: 'lots.stage.active',
  Inactive: 'lots.stage.inactive',
  Harvested: 'lots.stage.harvested',
  Planned: 'lots.stage.planned',
  Dormant: 'lots.stage.dormant'
}

const sourceFreshnessLabelKey: Record<LotStoryProjectionV1['readiness']['sourceFreshness'], TranslationKey> = {
  fresh: 'lots.story.readiness.sourceFreshness.fresh',
  stale: 'lots.story.readiness.sourceFreshness.stale',
  unknown: 'lots.story.readiness.sourceFreshness.unknown'
}

const lotHistoryEventLabelKey: Record<LotEventType, TranslationKey> = {
  create: 'lots.history.eventType.create',
  update: 'lots.history.eventType.update',
  split: 'lots.history.eventType.split',
  merge: 'lots.history.eventType.merge',
  move: 'lots.history.eventType.move',
  transfer: 'lots.history.eventType.transfer'
}

const lotStoryCardStyle: React.CSSProperties = {
  border: '1px solid #dbeafe',
  borderRadius: 10,
  backgroundColor: '#ffffff',
  padding: 12,
  display: 'grid',
  gap: 4
}

const uniqueStrings = (values: string[]): string[] => {
  const seen = new Set<string>()
  const out: string[] = []

  values.forEach((value) => {
    const normalized = String(value ?? '').trim()
    if (!normalized || seen.has(normalized)) return
    seen.add(normalized)
    out.push(normalized)
  })

  return out
}

const formatHistoryTimestamp = (value: string): string => {
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString()
}

const getLotHistoryBadgeStyle = (eventType: LotEventType): React.CSSProperties => {
  if (eventType === 'create') return { backgroundColor: '#dcfce7', color: '#166534' }
  if (eventType === 'update') return { backgroundColor: '#dbeafe', color: '#1d4ed8' }
  if (eventType === 'split') return { backgroundColor: '#ede9fe', color: '#6d28d9' }
  if (eventType === 'merge') return { backgroundColor: '#fae8ff', color: '#a21caf' }
  if (eventType === 'move') return { backgroundColor: '#cffafe', color: '#0f766e' }
  if (eventType === 'transfer') return { backgroundColor: '#ffedd5', color: '#c2410c' }
  return { backgroundColor: '#e5e7eb', color: '#374151' }
}

const getLotStoryReasonBadgeStyle = (severity: 'warning' | 'block'): React.CSSProperties => (
  severity === 'block'
    ? { backgroundColor: '#fee2e2', color: '#b91c1c' }
    : { backgroundColor: '#fef3c7', color: '#92400e' }
)

const readinessCueLevelLabelKey: Record<LotStoryReadinessCueLevel, TranslationKey> = {
  strong: 'lots.story.readinessCue.strong',
  caution: 'lots.story.readinessCue.caution',
  weak: 'lots.story.readinessCue.weak'
}

const readinessCueReasonLabelKey: Record<LotStoryReadinessCueReason, TranslationKey> = {
  'fully-ready': 'lots.story.readinessCue.reason.fully-ready',
  'has-blocking-readiness-reasons': 'lots.story.readinessCue.reason.has-blocking-readiness-reasons',
  'pending-lineage-sync': 'lots.story.readinessCue.reason.pending-lineage-sync',
  'ambiguous-legacy-matches': 'lots.story.readinessCue.reason.ambiguous-legacy-matches',
  'source-data-stale': 'lots.story.readinessCue.reason.source-data-stale',
  'has-warning-readiness-reasons': 'lots.story.readinessCue.reason.has-warning-readiness-reasons',
  'unresolved-evidence': 'lots.story.readinessCue.reason.unresolved-evidence',
  'source-freshness-unknown': 'lots.story.readinessCue.reason.source-freshness-unknown'
}

const getReadinessCueBadgeStyle = (level: LotStoryReadinessCueLevel): React.CSSProperties => {
  if (level === 'strong') return { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#bbf7d0' }
  if (level === 'caution') return { backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fde68a' }
  return { backgroundColor: '#fee2e2', color: '#b91c1c', borderColor: '#fecaca' }
}

const formatStoryLocationRef = (
  locationType?: LotLocationType,
  locationId?: string,
  farmId?: string
): string | undefined => {
  const normalizedLocationId = locationId?.trim()
  if (!locationType && !normalizedLocationId) return undefined
  if (locationType === 'farm') return normalizedLocationId ?? farmId?.trim() ?? undefined
  if (locationType === 'production-unit') return normalizedLocationId ?? undefined
  if (locationType === 'processing-site') return normalizedLocationId ?? farmId?.trim() ?? undefined
  return normalizedLocationId
}

const formatStorySiteSummary = (
  site: LotStoryProjectionV1['identityContext']['currentSite'] | undefined,
  t: TranslateFn
): string => {
  if (!site) return t('lots.story.summary.noCurrentSite')

  return site.displayName?.trim()
    || formatStoryLocationRef(site.locationType, site.locationId, site.farmId)
    || t('lots.story.summary.noCurrentSite')
}

const formatStoryDescendantSite = (descendant: LotStoryProjectionV1['identityContext']['activeDescendants'][number]): string | undefined => (
  formatStoryLocationRef(descendant.locationType, descendant.locationId, descendant.farmId)
)

const formatStoryDisposition = (projection: LotStoryProjectionV1, t: TranslateFn): string => (
  projection.performanceSummary.descendantDisposition
    ? t('lots.story.metrics.dispositionValue', {
        active: projection.performanceSummary.descendantDisposition.activeCount,
        inactive: projection.performanceSummary.descendantDisposition.inactiveCount,
        harvested: projection.performanceSummary.descendantDisposition.harvestedCount,
        other: projection.performanceSummary.descendantDisposition.otherCount
      })
    : '—'
)

export default function LotStoryCollaborationPreview({
  projection,
  t
}: Readonly<LotStoryCollaborationPreviewProps>) {
  const accentText = '#1d4ed8'
  const accentBorder = '#bfdbfe'
  const storyFamilyLabel = getStoryFamilyLabel(projection)
  const storyFamilyTechnicalId = getStoryFamilyTechnicalId(projection)
  const summaryValueStyle: React.CSSProperties = { fontSize: 14, color: '#111827' }
  const metricValueStyle: React.CSSProperties = { fontSize: 18, fontWeight: 700, color: '#111827' }
  const readinessCue = deriveLotStoryReadinessCue(projection.readiness)
  const readinessCueLevelLabel = t(readinessCueLevelLabelKey[readinessCue.level])
  const readinessCueReasonLabel = t(readinessCueReasonLabelKey[readinessCue.reason])
  const readinessCueBadgeStyle = getReadinessCueBadgeStyle(readinessCue.level)

  return (
    <div style={{ display: 'grid', gap: 12 }}>
      <div
        style={{
          border: `1px solid ${readinessCueBadgeStyle.borderColor ?? accentBorder}`,
          borderRadius: 10,
          backgroundColor: readinessCueBadgeStyle.backgroundColor,
          padding: 12,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 10
        }}
      >
        <span
          style={{
            padding: '2px 10px',
            borderRadius: 999,
            fontSize: 11,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
            backgroundColor: '#ffffff',
            color: readinessCueBadgeStyle.color
          }}
        >
          {readinessCueLevelLabel}
        </span>
        <span style={{ fontSize: 13, color: readinessCueBadgeStyle.color, fontWeight: 600 }}>
          {t('lots.story.readinessCue.title', { value: readinessCueLevelLabel })}
        </span>
        <span style={{ fontSize: 13, color: readinessCueBadgeStyle.color }}>{readinessCueReasonLabel}</span>
      </div>
      {projection.readiness.reasons.length > 0 ? (
        <div style={{ display: 'grid', gap: 8 }}>
          <strong style={{ fontSize: 13, color: accentText }}>{t('lots.story.reasons.title')}</strong>
          <div style={{ display: 'grid', gap: 8 }}>
            {projection.readiness.reasons.map((reason, index) => (
              <div
                key={`${reason.code}-${index}`}
                style={{
                  border: reason.severity === 'block' ? '1px solid #fecaca' : `1px solid ${accentBorder}`,
                  borderRadius: 10,
                  backgroundColor: '#ffffff',
                  padding: 12,
                  display: 'grid',
                  gap: 6
                }}
              >
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                  <span style={{ ...getLotStoryReasonBadgeStyle(reason.severity), padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                    {t(reason.severity === 'block' ? 'lots.story.reason.block' : 'lots.story.reason.warning')}
                  </span>
                  <span style={{ fontSize: 13, color: '#111827' }}>{reason.message}</span>
                </div>
                {reason.sourceRefs && reason.sourceRefs.length > 0 ? (
                  <div style={{ fontSize: 12, color: accentText }}>{uniqueStrings(reason.sourceRefs).join(', ')}</div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
        <div style={lotStoryCardStyle}>
          <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.summary.lineageRoot')}</strong>
          <div style={{ display: 'grid', gap: 4 }}>
            <div style={{ ...summaryValueStyle, wordBreak: 'break-word' }}>{storyFamilyLabel}</div>
            {storyFamilyTechnicalId ? (
              <div style={{ fontSize: 12, color: accentText, wordBreak: 'break-word' }}>{storyFamilyTechnicalId}</div>
            ) : null}
          </div>
        </div>
        <div style={lotStoryCardStyle}>
          <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.summary.sourceTenant')}</strong>
          <div style={summaryValueStyle}>{projection.identityContext.sourceTenantKey || '—'}</div>
        </div>
        <div style={lotStoryCardStyle}>
          <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.summary.origin')}</strong>
          <div style={summaryValueStyle}>{projection.identityContext.lineageOrigin?.lotKey ?? projection.identityContext.lineageOrigin?.lotId ?? '—'}</div>
          <div style={{ fontSize: 12, color: accentText }}>
            {projection.identityContext.lineageOrigin?.recordedAt
              ? formatHistoryTimestamp(projection.identityContext.lineageOrigin.recordedAt)
              : '—'}
          </div>
        </div>
        <div style={lotStoryCardStyle}>
          <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.summary.currentSite')}</strong>
          <div style={summaryValueStyle}>{formatStorySiteSummary(projection.identityContext.currentSite, t)}</div>
        </div>
        <div style={lotStoryCardStyle}>
          <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.summary.species')}</strong>
          <div style={summaryValueStyle}>{formatOptionalStoryText(projection.identityContext.species)}</div>
        </div>
        <div style={lotStoryCardStyle}>
          <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.summary.currentStage')}</strong>
          <div style={summaryValueStyle}>{projection.identityContext.stage ? t(stageLabelKey[projection.identityContext.stage]) : '—'}</div>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        <strong style={{ fontSize: 13, color: accentText }}>{t('lots.story.summary.activeDescendantsList')}</strong>
        {projection.identityContext.activeDescendants.length === 0 ? (
          <div style={{ fontSize: 13, color: accentText }}>{t('lots.story.summary.activeDescendantsEmpty')}</div>
        ) : (
          <div style={{ display: 'grid', gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
            {projection.identityContext.activeDescendants.map((descendant) => (
              <div key={descendant.lotId} style={lotStoryCardStyle}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 14, color: '#111827' }}>{descendant.lotKey}</strong>
                  <span style={{ fontSize: 12, color: accentText }}>{descendant.status ? t(stageLabelKey[descendant.status]) : '—'}</span>
                </div>
                {formatStoryDescendantSite(descendant) ? (
                  <div style={{ fontSize: 12, color: accentText }}>{formatStoryDescendantSite(descendant)}</div>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        <strong style={{ fontSize: 13, color: accentText }}>{t('lots.story.metrics.title')}</strong>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.metrics.timelineEvents')}</strong>
            <div style={metricValueStyle}>{projection.lifecycleTimeline.length}</div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.metrics.feedLogs')}</strong>
            <div style={metricValueStyle}>{formatOptionalStoryNumber(projection.performanceSummary.actualFeed?.logCount)}</div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.metrics.waterLogs')}</strong>
            <div style={metricValueStyle}>{formatOptionalStoryNumber(projection.performanceSummary.waterContextSummary?.logCount)}</div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.metrics.productionRecords')}</strong>
            <div style={metricValueStyle}>{formatOptionalStoryNumber(projection.performanceSummary.productionOutcomeSummary?.recordCount)}</div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.metrics.currentBiomassKg')}</strong>
            <div style={metricValueStyle}>{formatOptionalStoryNumber(projection.performanceSummary.biomassTrend?.currentBiomassKg)}</div>
          </div>
        </div>
      </div>

      <div style={lotStoryCardStyle}>
        <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.metrics.disposition')}</strong>
        <div style={{ fontSize: 13, color: '#111827' }}>{formatStoryDisposition(projection, t)}</div>
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        <strong style={{ fontSize: 13, color: accentText }}>{t('lots.story.readinessMetadata.title')}</strong>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.readinessMetadata.status')}</strong>
            <div style={summaryValueStyle}>{getLotStoryReadinessLabel(t, projection.readiness.status)}</div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.readinessMetadata.sourceFreshness')}</strong>
            <div style={summaryValueStyle}>{t(sourceFreshnessLabelKey[projection.readiness.sourceFreshness])}</div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.readinessMetadata.pendingLineageSync')}</strong>
            <div style={summaryValueStyle}>{projection.readiness.pendingLineageSync ? t('lots.story.value.yes') : t('lots.story.value.no')}</div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.readinessMetadata.unresolvedEvidence')}</strong>
            <div style={summaryValueStyle}>{projection.readiness.unresolvedEvidenceCount}</div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.readinessMetadata.ambiguousLegacyMatches')}</strong>
            <div style={summaryValueStyle}>{projection.readiness.ambiguousLegacyMatchCount}</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        <strong style={{ fontSize: 13, color: accentText }}>{t('lots.story.evidence.title')}</strong>
        <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.evidence.feedRefs')}</strong>
            <div style={summaryValueStyle}>{projection.evidencePackage.feedRefs.length}</div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.evidence.waterRefs')}</strong>
            <div style={summaryValueStyle}>{projection.evidencePackage.waterRefs.length}</div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.evidence.productionRefs')}</strong>
            <div style={summaryValueStyle}>{projection.evidencePackage.productionRefs.length}</div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.evidence.sourceLotRefs')}</strong>
            <div style={summaryValueStyle}>{projection.sourceRefs.sourceLotIds.length}</div>
          </div>
          <div style={lotStoryCardStyle}>
            <strong style={{ fontSize: 12, color: '#92400e' }}>{t('lots.story.evidence.sourceEventRefs')}</strong>
            <div style={summaryValueStyle}>{projection.sourceRefs.sourceEventIds.length}</div>
          </div>
        </div>
      </div>

      <div style={{ display: 'grid', gap: 8 }}>
        <strong style={{ fontSize: 13, color: accentText }}>{t('lots.story.timeline.title')}</strong>
        {projection.lifecycleTimeline.length === 0 ? (
          <div style={{ fontSize: 13, color: accentText }}>{t('lots.story.timeline.empty')}</div>
        ) : (
          <div style={{ display: 'grid', gap: 12 }}>
            {projection.lifecycleTimeline.map((event) => {
              const fromLocation = formatStoryLocationRef(event.fromLocationType, event.fromLocationId, event.fromFarmId)
              const toLocation = formatStoryLocationRef(event.toLocationType, event.toLocationId, event.toFarmId)

              return (
                <article key={event.eventId} style={{ border: `1px solid ${accentBorder}`, borderRadius: 10, backgroundColor: '#ffffff', padding: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
                    <span style={{ ...getLotHistoryBadgeStyle(event.eventType), padding: '4px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.3 }}>
                      {t(lotHistoryEventLabelKey[event.eventType])}
                    </span>
                    <span style={{ fontSize: 12, color: accentText }}>{formatHistoryTimestamp(event.recordedAt)}</span>
                  </div>

                  <div style={{ display: 'grid', gap: 8, fontSize: 13, color: '#111827' }}>
                    {event.sourceLotIds.length > 0 ? <div><strong>{t('lots.history.sourceLots')}</strong> {uniqueStrings(event.sourceLotIds).join(', ')}</div> : null}
                    {event.targetLotIds.length > 0 ? <div><strong>{t('lots.history.targetLots')}</strong> {uniqueStrings(event.targetLotIds).join(', ')}</div> : null}
                    {fromLocation ? <div><strong>{t('lots.story.location.from')}</strong> {fromLocation}</div> : null}
                    {toLocation ? <div><strong>{t('lots.story.location.to')}</strong> {toLocation}</div> : null}
                    {event.notes ? <div><strong>{t('lots.history.notes')}</strong> {event.notes}</div> : null}
                  </div>
                </article>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}